<template>
  <v-app>
    <the-left-sidebar />

    <the-top-toolbar
      v-if="
        $vuetify.breakpoint.smAndDown &&
          !tools.print.active &&
          !tools.drawing.active &&
          !tools.measure.active
      "
    />

    <v-content
      :style="{
        marginLeft:
          showSideMenu || !sidebarHasOpened || $vuetify.breakpoint.smAndDown
            ? 0
            : 56 + 'px',
      }"
    >
      <v-container
        fluid
        fill-height
        class="content-container"
        :class="$vuetify.breakpoint.smAndDown ? 'content-container-mobile' : ''"
      >
        <v-layout column wrap>
          <Split
            direction="vertical"
            class="the-split-panel"
            @onDragEnd="onDragEnd"
            :class="{ hideGutter: !showTabs }"
          >
            <SplitArea :size="mapHeight" :minSize="mapSplitPaneMinSize">
              <div class="map-container">
                <the-map />
              </div>
            </SplitArea>
            <SplitArea
              v-show="tabsAreOpen"
              :minSize="bottomTabsSplitPaneMinSize"
              :size="bottomTabsHeight"
            >
              <the-bottom-tabs />
            </SplitArea>
          </Split>
        </v-layout>

        <layer-add-dialog />

        <image-gallery />
      </v-container>
    </v-content>

    <v-tooltip left v-if="$vuetify.breakpoint.mdAndUp">
      <template v-slot:activator="{ on }">
        <v-btn
          v-show="!tools.print.active"
          v-on="on"
          fab
          label="Vis kartkatalog"
          aria-label="Vis kartkatalog"
          small
          v-bind:style="{ top: themeProfile ? '50px' : '10px' }"
          style="position: absolute; right: 11px;"
          @click="showCatalogueDrawer"
        >
          <v-icon>mdi-file-tree</v-icon>
        </v-btn>
      </template>
      <span>Vis kartkatalog</span>
    </v-tooltip>

    <v-tooltip left v-if="$vuetify.breakpoint.mdAndUp">
      <template v-slot:activator="{ on }">
        <v-btn
          v-if="showPredefinedBookmarks"
          v-show="!tools.print.active"
          v-on="on"
          fab
          small
          v-bind:style="{ top: themeProfile ? '95px' : '40px' }"
          style="position: absolute; right: 11px;"
          @click="showPredefinedBookmarksDrawer"
        >
          <v-icon>mdi-map-marker-radius</v-icon>
        </v-btn>
      </template>
      <span>Vis risikoobjekter</span>
    </v-tooltip>

    <layer-info-card
      v-if="$vuetify.breakpoint.mdAndUp"
      v-show="!tools.print.active && currentlyOpenedProject === null"
    />

    <layer-info-drawer v-if="mapLoaded" />

    <layer-catalogue v-show="showCatalogue" />

    <map-predefined-bookmarks
      v-if="showPredefinedBookmarks"
      v-show="showPredefinedBookmarks"
    />

    <the-welcome-dialog
      v-if="$vuetify.breakpoint.mdAndUp && currentlyOpenedProject === null"
    />

    <confirm-dialog
      :active="confirmGoToPreviousVersionDialogOpen"
      :text="confirmGoToPreviousVersionDialogText"
      title="Gå til forrige versjon"
      confirm-text="Gå til forrige versjon av løsningen"
      reject-text="Bli her"
      @dialog-confirmed="openSavedMapInPreviousVersion"
      @dialog-rejected="confirmGoToPreviousVersionDialogOpen = false"
    />

    <confirm-dialog
      :active="showTestWarningDialog"
      :text="
        'Du har åpnet en betaversjon av' +
          appTitle +
          '. Denne versjonen kan inneholde eksperimentell funksjonalitet og utdaterte kartdata. '
      "
      title="Viktig melding"
      confirm-text="Gå til offisiell versjon"
      reject-text="Bli her"
      @dialog-confirmed="goToOfficialVersion"
      @dialog-rejected="showTestWarningDialog = false"
    />

    <confirm-dialog
      v-if="startupDialog"
      :active="startupDialog.active"
      :text="startupDialog.text"
      :title="startupDialog.title"
      :confirm-text="startupDialog.confirmButtonText"
      :show-reject-button="false"
      @dialog-confirmed="hideStartupDialog"
    />

    <the-snack-bar />

    <app-tour />

    <the-app-drawer />
  </v-app>
</template>

<script>
import TheLeftSidebar from "@/pages/MapPage/Layout/TheLeftSidebar";
import TheAppDrawer from "@/pages/MapPage/Layout/TheAppDrawer";
import LayerInfoCard from "@/pages/MapPage/Tools/Layers/LayerInfoCard";
import ConfirmDialog from "@/components/ConfirmDialog";
import { mapState, mapActions } from "vuex";
import MappingMixin from "@/mixins/mappingMixin";
import MapPredefinedBookmarks from "./Tools/Bookmarks/MapPredefinedBookmarks.vue";

export default {
  name: "TheMapPage",
  components: {
    TheAppDrawer,
    ConfirmDialog,
    TheMap: () => import("@/pages/MapPage/Map/TheMap.vue"),
    LayerAddDialog: () => import("@/pages/MapPage/Tools/Layers/LayerAddDialog"),
    TheLeftSidebar,
    LayerInfoDrawer: () =>
      import("@/pages/MapPage/Tools/Layers/LayerInfoDrawer"),
    LayerCatalogue: () => import("@/pages/MapPage/Tools/Layers/LayerCatalogue"),
    ImageGallery: () => import("@/pages/MapPage/Tools/Misc/ImageGallery"),
    TheBottomTabs: () => import("@/pages/MapPage/Layout/TheBottomTabs"),
    TheSnackBar: () => import("@/components/TheSnackBar"),
    LayerInfoCard,
    AppTour: () => import("@/pages/MapPage/Tools/Tips/AppTour"),
    TheTopToolbar: () => import("@/pages/MapPage/Layout/TheTopToolbar"),
    TheWelcomeDialog: () => import("@/pages/MapPage/Layout/TheWelcomeDialog"),
    MapPredefinedBookmarks,
  },
  mixins: [MappingMixin],
  watch: {
    mapLoaded(val) {
      if ((val && this.id) || this.$route.name === "link") {
        // If user tries to open an old project link then he will be redirected to the old version
        if (
          this.$route.name === "link" &&
          this.$route.query.hasOwnProperty("p")
        ) {
          this.confirmGoToPreviousVersionDialogOpen = true;
        } else if (
          this.$route.name === "link" &&
          this.$route.path.includes("metadata.html")
        ) {
          this.openMetadataInPreviousVersion();
        } else {
          let options = {
            id: this.id ? this.id : this.$route.query.link,
            showInfoMenu: true,
          };

          if (this.$route.query.hasOwnProperty("layer")) {
            options.zoomToQuery = {
              layer: this.$route.query.layer,
              field: this.$route.query.field,
              value: this.$route.query.value,
              buffer: this.$route.query.buffer,
              useCountiesAndMunicipalities: this.$route.query.ucm == "true",
            };
          }

          if (this.$route.query.hasOwnProperty("point")) {
            options.zoomToPoint = {
              x: parseInt(this.$route.query.x),
              y: parseInt(this.$route.query.y),
              zoom: parseInt(this.$route.query.zoom),
              wkid: parseInt(this.$route.query.wkid),
            };
          }

          if (options.id.split(",").length > 1) {
            let profilesList = options.id.split(",");
            options.id = profilesList[0];
            profilesList.shift();
            options.customAddedProfiles = profilesList;
          }
          this.addMapProfilesAndAddedProfiles(options);
        }
      }

      if (val && this.projectId) {
        this.getProjectByID(this.projectId).then((project) => {
          this.openSavedMap({
            map: project,
            allowSave: this.editHash === this.projectId.hashCode().toString(),
          });
        });
      }
    },
  },
  props: ["id", "projectId", "editHash"],
  computed: {
    ...mapState([
      "sidebarHasOpened",
      "showSideMenu",
      "bottomTabsHeight",
      "showAppDrawer",
      "startupDialog",
      "tools",
      "theMap",
      "currentlyOpenedProject",
      "showCatalogue",
      "showPredefinedBookmarks",
      "appTitle",
    ]),
    themeProfile: function() {
      return this.$store.getters["getAddedMapProfile"](this.selectedTheme);
    },
    showPredefinedBookmarks: function() {
      return process.env.VUE_APP_SHOW_PREDEFINED_BOOKMARKS === "show";
    },
    ...mapState(["mapLoaded", "tabsAreOpen", "appHelpEmailSubject"]),
    ...mapState(["showImageGallery"]),
    confirmGoToPreviousVersionDialogText: function() {
      if (this.$route.query.hasOwnProperty("p")) {
        return (
          "<p>Du prøver å åpne et lagret kart som ble laget i forrige versjonen av løsningen. For å åpne dette kartet må du gå til forrige versjon.</p>" +
          "<a href='mailto:gisstotte@nve.no?Subject=" +
          this.appHelpEmailSubject +
          "&body=Jeg ønsker å overføre kart med id " +
          this.$route.query.p +
          " til dagens versjon.' target='_top'>Send oss gjerne en epost</a> dersom du ønsker å overføre kartet til dagens versjon av løsningen så hjelper vi deg med dette."
        );
      } else {
        return "";
      }
    },
    showTabs: {
      set(showTabs) {
        this.setShowTabs(showTabs);
      },
      get() {
        return this.$store.state.showTabs;
      },
    },
    useMapMarginFix: function() {
      return this.showSideMenu && this.showAppDrawer;
    },
    mapHeight: {
      set(mapHeight) {
        this.setMapHeight(mapHeight);
      },
      get() {
        return this.$store.state.mapHeight;
      },
    },
  },
  data() {
    return {
      mapSplitPaneMinSize: 300,
      bottomTabsSplitPaneMinSize: 48,
      confirmGoToPreviousVersionDialogOpen: false,
      showTestWarningDialog: false,
      showProfileInfoDialog: false,
    };
  },
  mounted() {
    this.showTestWarningDialog =
      process.env.VUE_APP_SHOW_TEST_WARNING === "true";
    this.clearCurrentlyOpenedProject();
    String.prototype.hashCode = function() {
      let hash = 0,
        i,
        chr;
      if (this.length === 0) return hash;
      for (i = 0; i < this.length; i++) {
        chr = this.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0;
      }
      return hash;
    };

    if (this.mapLoaded) {
      if (this.id) {
        this.setShowAppDrawer(false);
        this.setShowSideMenu(false);
        this.removeAllMapProfiles();
        this.addMapProfilesAndAddedProfiles({
          id: this.id,
          showInfoMenu: true,
        });
      } else {
        this.getProjectByID(this.projectId).then((project) => {
          this.openSavedMap({
            map: project,
            allowSave: this.editHash === this.projectId.hashCode().toString(),
          });
        });
      }
    } else {
      this.initStore();
    }

    if (this.id) {
      this.setExplorerVisible(false);
    }
  },
  methods: {
    ...mapActions([
      "initStore",
      "setShowAppDrawer",
      "setShowSideMenu",
      "enableSave",
      "disableSave",
      "setShowPredefinedBookmarks",
      "setShowCatalogue",
      "clearCurrentlyOpenedProject",
      "setHideWelcomeDialogFromLocalStorage",
      "setHide3dNavigationHelpDialogFromLocalStorage",
      "setStartupDialog",
      "setExplorerVisible",
    ]),
    ...mapActions(["hideExplorer"]),
    showCatalogueDrawer() {
      this.setShowCatalogue(true);
    },
    showPredefinedBookmarksDrawer() {
      this.setShowPredefinedBookmarks(true);
    },
    openSavedMapInPreviousVersion() {
      window.location.href =
        process.env.VUE_APP_PREVIOUS_VERSION_URL + "?p=" + this.$route.query.p;
    },
    hideStartupDialog() {
      this.setStartupDialog(null);
    },
    openMetadataInPreviousVersion() {
      window.location.href =
        process.env.VUE_APP_PREVIOUS_VERSION_URL +
        this.$route.fullPath.substring(5);
    },
    goToOfficialVersion() {
      window.location.href = process.env.VUE_APP_OFFICIAL_VERSION_URL;
    },
    onDragEnd(size) {
      this.setMapHeight(size[0]);
      this.setBottomTabsHeight(size[1]);
    },
  },
  beforeDestroy() {
    this.removeAllMapProfiles();
    this.setIs3d(false);
  },
  created() {
    this.setHideWelcomeDialogFromLocalStorage();
    this.setHide3dNavigationHelpDialogFromLocalStorage();
  },
};
</script>
<style>
.drawer-button {
  margin-bottom: 10px;
  margin-top: 10px;
}

.drawer-icon {
  font-size: 22px !important;
}

.drawer-content {
  margin-left: 56px;
}

.drawer-icon-active {
  padding-left: 5px;
  margin-left: 14px;
  margin-right: 14px;
  margin-top: -15px;
  margin-bottom: 12px;
  border-bottom: 3px solid deepskyblue;
}

div.hideGutter div.gutter.gutter-vertical {
  display: none !important;
}

.the-split-panel {
  height: 100%;
}

.right-navigation-drawer {
  z-index: 9;
  background: #eceff1;
}

.map-container {
  position: relative;
  height: 100%;
}

.map-fix-margin {
  margin-left: 456px !important;
}
</style>
